import React, { useState, useEffect, useCallback } from 'react';

const API_URL = 'https://opmlee572h.execute-api.us-west-2.amazonaws.com';
const ADMIN_PASSWORD = 'ADMIN_PASSWORD_HERE'; // ← Replace before pushing

const s = {
  card: { background: '#1e293b', border: '1px solid #334155', borderRadius: '16px', padding: '1.5rem', boxShadow: '0 4px 24px rgba(0,0,0,0.3)' },
  label: { fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b', marginBottom: '0.5rem', display: 'block' },
  badge: (color) => ({ background: color + '18', border: `1px solid ${color}35`, color, borderRadius: '20px', fontSize: '0.65rem', fontWeight: 700, padding: '0.15rem 0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em' }),
  input: { background: '#0f172a', border: '1.5px solid #334155', borderRadius: '10px', padding: '0.75rem 1rem', color: 'white', fontSize: '0.875rem', outline: 'none', fontFamily: 'DM Sans, sans-serif', width: '100%', boxSizing: 'border-box' },
};

function StatCard({ label, value, sub, color = '#4ade80', icon }) {
  return (
    <div style={{ ...s.card, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`, pointerEvents: 'none' }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <span style={s.label}>{label}</span>
        <span style={{ fontSize: '1.1rem' }}>{icon}</span>
      </div>
      <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '2rem', fontWeight: 800, color, lineHeight: 1, marginBottom: '0.3rem' }}>{value}</p>
      {sub && <p style={{ fontSize: '0.72rem', color: '#475569' }}>{sub}</p>}
    </div>
  );
}

function AdminPage() {
  const [authed,        setAuthed]        = useState(false);
  const [pwInput,       setPwInput]       = useState('');
  const [pwError,       setPwError]       = useState('');
  const [activeTab,     setActiveTab]     = useState('overview');
  const [toast,         setToast]         = useState('');

  // Disputes
  const [disputes,      setDisputes]      = useState([]);
  const [disputeFilter, setDisputeFilter] = useState('open');
  const [expanded,      setExpanded]      = useState(null);
  const [resolving,     setResolving]     = useState({});
  const [adminNote,     setAdminNote]     = useState({});

  // Providers
  const [providers,     setProviders]     = useState([]);
  const [provSearch,    setProvSearch]    = useState('');
  const [provFilter,    setProvFilter]    = useState('all');

  // Bookings
  const [bookings,      setBookings]      = useState([]);

  // Loading states
  const [loadingDisputes,  setLoadingDisputes]  = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [loadingBookings,  setLoadingBookings]  = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('vyblo_admin') === 'true') setAuthed(true);
  }, []);

  useEffect(() => {
    if (!authed) return;
    fetchDisputes();
    fetchProviders();
  }, [authed]);

  useEffect(() => {
    if (authed) fetchDisputes();
  }, [disputeFilter]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  const fetchDisputes = async () => {
    setLoadingDisputes(true);
    try {
      const res  = await fetch(`${API_URL}/disputes`);
      const data = await res.json();
      setDisputes(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); }
    setLoadingDisputes(false);
  };

  const fetchProviders = async () => {
    setLoadingProviders(true);
    try {
      const res  = await fetch(`${API_URL}/providers`);
      const data = await res.json();
      setProviders(Array.isArray(data) ? data : []);
    } catch (err) { console.error(err); }
    setLoadingProviders(false);
  };

  const fetchBookingsForProvider = async (providerId) => {
    setLoadingBookings(true);
    try {
      const res  = await fetch(`${API_URL}/bookings?providerId=${providerId}`);
      const data = await res.json();
      setBookings(prev => [...prev.filter(b => b.providerId !== providerId), ...(Array.isArray(data) ? data : [])]);
    } catch {}
    setLoadingBookings(false);
  };

  const handleLogin = () => {
    if (pwInput === ADMIN_PASSWORD) {
      setAuthed(true);
      sessionStorage.setItem('vyblo_admin', 'true');
      setPwError('');
    } else {
      setPwError('Incorrect password.');
      setPwInput('');
    }
  };

  const handleResolve = async (dispute, outcome) => {
    if (!window.confirm(`Are you sure you want to ${outcome === 'refund' ? 'refund the client' : 'release payment to the provider'}?`)) return;
    setResolving(prev => ({ ...prev, [dispute.bookingId]: outcome }));
    try {
      const res  = await fetch(`${API_URL}/resolve-dispute`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookingId: dispute.bookingId, providerId: dispute.providerId, outcome, adminNote: adminNote[dispute.bookingId] || '' }),
      });
      const data = await res.json();
      if (res.ok) { showToast(`✓ ${data.message}`); fetchDisputes(); setExpanded(null); }
      else alert(data.error || 'Failed to resolve');
    } catch (err) { alert('Error: ' + err.message); }
    setResolving(prev => ({ ...prev, [dispute.bookingId]: null }));
  };

  const handleDeleteProvider = async (providerId, name) => {
    if (!window.confirm(`Permanently delete provider "${name}"? This removes their listing, services, and deals. Bookings will be cancelled.`)) return;
    try {
      const res = await fetch(`${API_URL}/providers?providerId=${providerId}`, { method: 'DELETE' });
      if (res.ok) { showToast(`✓ Provider "${name}" deleted`); fetchProviders(); }
      else { const d = await res.json(); alert(d.error || 'Failed to delete'); }
    } catch (err) { alert('Error: ' + err.message); }
  };

  // ── Derived stats ──────────────────────────────────────────────────────────
  const openDisputes    = disputes.filter(d => d.disputeStatus === 'open');
  const resolvedDisputes = disputes.filter(d => d.disputeStatus === 'resolved');
  const verifiedProviders = providers.filter(p => p.verified);
  const connectedProviders = providers.filter(p => p.stripeOnboardingComplete);
  const totalServices   = providers.reduce((s, p) => s + (p.services?.length || 0), 0);

  const filteredDisputes = disputes.filter(d => {
    if (disputeFilter === 'open') return d.disputeStatus === 'open';
    if (disputeFilter === 'resolved') return d.disputeStatus === 'resolved';
    return true;
  });

  const filteredProviders = providers.filter(p => {
    const q = provSearch.toLowerCase();
    const matchSearch = !q || p.name?.toLowerCase().includes(q) || p.location?.toLowerCase().includes(q) || p.serviceType?.toLowerCase().includes(q);
    if (provFilter === 'verified') return matchSearch && p.verified;
    if (provFilter === 'connected') return matchSearch && p.stripeOnboardingComplete;
    if (provFilter === 'unverified') return matchSearch && !p.verified;
    return matchSearch;
  });

  // ── Login screen ───────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', fontFamily: 'DM Sans, sans-serif' }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap');`}</style>
        <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '24px', padding: '2.5rem', width: '100%', maxWidth: '400px', boxShadow: '0 32px 64px rgba(0,0,0,0.6)' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ width: 60, height: 60, borderRadius: '16px', background: 'linear-gradient(135deg, #dc2626, #b91c1c)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', margin: '0 auto 1rem', boxShadow: '0 8px 24px rgba(220,38,38,0.4)' }}>🛡️</div>
            <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', fontWeight: 800, color: 'white', marginBottom: '0.3rem' }}>Vyblo Admin</h1>
            <p style={{ color: '#64748b', fontSize: '0.875rem' }}>Dispute Management Console</p>
          </div>
          <div>
            <label style={s.label}>Admin Password</label>
            <input type="password" value={pwInput} onChange={e => setPwInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleLogin()} placeholder="Enter password" autoFocus
              style={{ ...s.input, borderColor: pwError ? '#dc2626' : '#334155', marginBottom: '0.5rem' }}
              onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = pwError ? '#dc2626' : '#334155'}
            />
            {pwError && <p style={{ color: '#dc2626', fontSize: '0.78rem', marginBottom: '1rem' }}>⚠️ {pwError}</p>}
            <button onClick={handleLogin} style={{ width: '100%', background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: 'white', border: 'none', borderRadius: '10px', padding: '0.875rem', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', marginTop: '0.5rem', boxShadow: '0 4px 16px rgba(220,38,38,0.3)', transition: 'all 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 8px 24px rgba(220,38,38,0.45)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(220,38,38,0.3)'}
            >Access Admin Console →</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Admin dashboard ────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', color: 'white', fontFamily: 'DM Sans, sans-serif' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700&display=swap'); * { box-sizing: border-box; }`}</style>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: '1.5rem', right: '1.5rem', background: '#1a5c3a', color: 'white', borderRadius: '12px', padding: '0.875rem 1.25rem', fontSize: '0.875rem', fontWeight: 600, zIndex: 9999, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', animation: 'fadeIn 0.2s ease' }}>
          {toast}
        </div>
      )}

      {/* Nav */}
      <nav style={{ background: '#1e293b', borderBottom: '1px solid #334155', padding: '0 2rem', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ width: 32, height: 32, borderRadius: '8px', background: 'linear-gradient(135deg, #dc2626, #b91c1c)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem' }}>🛡️</div>
          <div>
            <p style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1rem', color: 'white', lineHeight: 1 }}>Vyblo Admin</p>
            <p style={{ fontSize: '0.6rem', color: '#475569', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Management Console</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => { fetchDisputes(); fetchProviders(); }} style={{ background: '#334155', border: 'none', color: '#94a3b8', borderRadius: '8px', padding: '0.45rem 0.875rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>↻ Refresh</button>
          <button onClick={() => { sessionStorage.removeItem('vyblo_admin'); setAuthed(false); }} style={{ background: 'transparent', border: '1px solid #334155', color: '#64748b', borderRadius: '8px', padding: '0.45rem 0.875rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>Sign Out</button>
        </div>
      </nav>

      {/* Tab bar */}
      <div style={{ background: '#1e293b', borderBottom: '1px solid #334155', padding: '0 2rem', display: 'flex', gap: '0.25rem' }}>
        {[
          { key: 'overview',  label: '📊 Overview' },
          { key: 'disputes',  label: `⚠️ Disputes`, badge: openDisputes.length },
          { key: 'providers', label: '🏪 Providers' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{ background: 'transparent', border: 'none', borderBottom: activeTab === tab.key ? '2px solid #dc2626' : '2px solid transparent', color: activeTab === tab.key ? 'white' : '#64748b', fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem', fontWeight: 700, padding: '0.875rem 1.25rem', cursor: 'pointer', marginBottom: '-1px', display: 'flex', alignItems: 'center', gap: '0.4rem', transition: 'color 0.15s' }}>
            {tab.label}
            {tab.badge > 0 && <span style={{ background: '#dc2626', color: 'white', borderRadius: '99px', fontSize: '0.6rem', fontWeight: 800, padding: '1px 6px', minWidth: '18px', textAlign: 'center' }}>{tab.badge}</span>}
          </button>
        ))}
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div>
              <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', fontWeight: 800, color: 'white', marginBottom: '0.25rem' }}>Platform Overview</h1>
              <p style={{ color: '#64748b', fontSize: '0.875rem' }}>Live snapshot of all activity on Vyblo</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
              <StatCard label="Total Providers" value={providers.length} sub="active listings" color="#4ade80" icon="🏪" />
              <StatCard label="Verified Providers" value={verifiedProviders.length} sub={`${Math.round(verifiedProviders.length / Math.max(providers.length, 1) * 100)}% verified`} color="#60a5fa" icon="✓" />
              <StatCard label="Stripe Connected" value={connectedProviders.length} sub="accepting payments" color="#a78bfa" icon="💳" />
              <StatCard label="Open Disputes" value={openDisputes.length} sub={`${resolvedDisputes.length} resolved`} color={openDisputes.length > 0 ? '#f87171' : '#4ade80'} icon="⚠️" />
              <StatCard label="Total Services" value={totalServices} sub="across all providers" color="#fbbf24" icon="✦" />
            </div>

            {/* Provider health breakdown */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
              <div style={s.card}>
                <p style={{ ...s.label, marginBottom: '1.25rem' }}>Provider Health</p>
                {[
                  { label: 'Profile complete (photo + bio + location)', count: providers.filter(p => p.profilePicUrl && p.bio && p.location).length, total: providers.length, color: '#4ade80' },
                  { label: 'Have services listed', count: providers.filter(p => p.services?.length > 0).length, total: providers.length, color: '#60a5fa' },
                  { label: 'Identity verified', count: verifiedProviders.length, total: providers.length, color: '#a78bfa' },
                  { label: 'Stripe connected', count: connectedProviders.length, total: providers.length, color: '#fbbf24' },
                ].map((row, i) => (
                  <div key={i} style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                      <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{row.label}</span>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: row.color }}>{row.count}/{row.total}</span>
                    </div>
                    <div style={{ background: '#0f172a', borderRadius: '99px', height: '5px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${row.total > 0 ? (row.count / row.total * 100) : 0}%`, background: row.color, borderRadius: '99px', transition: 'width 0.8s ease' }} />
                    </div>
                  </div>
                ))}
              </div>

              <div style={s.card}>
                <p style={{ ...s.label, marginBottom: '1.25rem' }}>Dispute Summary</p>
                {openDisputes.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#475569' }}>
                    <p style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎉</p>
                    <p style={{ fontWeight: 600, color: '#4ade80' }}>No open disputes!</p>
                    <p style={{ fontSize: '0.78rem', marginTop: '0.3rem' }}>All clear right now</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                    {openDisputes.slice(0, 5).map((d, i) => (
                      <div key={i} onClick={() => { setActiveTab('disputes'); setExpanded(d.bookingId); }} style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: '10px', padding: '0.75rem 1rem', cursor: 'pointer', transition: 'all 0.2s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(220,38,38,0.14)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(220,38,38,0.08)'}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <p style={{ fontSize: '0.82rem', fontWeight: 700, color: 'white' }}>{d.service?.name || 'Service'}</p>
                          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#f87171' }}>${d.service?.price || '—'}</span>
                        </div>
                        <p style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '0.15rem' }}>
                          {d.disputeOpenedAt ? new Date(d.disputeOpenedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown date'} · <span style={{ color: '#f87171' }}>Open</span>
                        </p>
                      </div>
                    ))}
                    {openDisputes.length > 5 && <p style={{ fontSize: '0.75rem', color: '#64748b', textAlign: 'center', cursor: 'pointer' }} onClick={() => setActiveTab('disputes')}>+ {openDisputes.length - 5} more →</p>}
                  </div>
                )}
              </div>
            </div>

            {/* Recent providers */}
            <div style={s.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                <p style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, color: 'white', fontSize: '1rem' }}>Recent Providers</p>
                <button onClick={() => setActiveTab('providers')} style={{ background: 'transparent', border: 'none', color: '#60a5fa', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>View all →</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {providers.slice(0, 6).map((p, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', padding: '0.75rem', background: '#0f172a', borderRadius: '10px', border: '1px solid #1e293b' }}>
                    <div style={{ width: 36, height: 36, borderRadius: '10px', overflow: 'hidden', background: '#1e3a5f', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.875rem', fontWeight: 800, color: '#60a5fa' }}>
                      {p.profilePicUrl ? <img src={p.profilePicUrl} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (p.name?.[0] || '?')}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: '0.875rem', fontWeight: 700, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name || 'Unknown'}</p>
                      <p style={{ fontSize: '0.72rem', color: '#64748b' }}>{p.serviceType || p.service_type || '—'} {p.location ? `· ${p.location}` : ''}</p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                      {p.verified && <span style={s.badge('#4ade80')}>✓ ID</span>}
                      {p.stripeOnboardingComplete && <span style={s.badge('#a78bfa')}>💳</span>}
                      <span style={s.badge('#60a5fa')}>{p.services?.length || 0} svcs</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── DISPUTES TAB ── */}
        {activeTab === 'disputes' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div>
              <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', fontWeight: 800, color: 'white', marginBottom: '0.25rem' }}>Disputes</h1>
              <p style={{ color: '#64748b', fontSize: '0.875rem' }}>Review and resolve client-provider disputes</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
              <StatCard label="Open Disputes" value={openDisputes.length} sub="need review" color="#f87171" icon="⚠️" />
              <StatCard label="Resolved" value={resolvedDisputes.length} sub="all time" color="#4ade80" icon="✓" />
              <StatCard label="Total Disputes" value={disputes.length} sub="ever opened" color="#94a3b8" icon="📋" />
            </div>

            {/* Filter tabs */}
            <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '1px solid #334155', paddingBottom: '0' }}>
              {[{ key: 'open', label: '⚠️ Open', count: openDisputes.length }, { key: 'resolved', label: '✓ Resolved' }, { key: 'all', label: 'All' }].map(tab => (
                <button key={tab.key} onClick={() => setDisputeFilter(tab.key)}
                  style={{ background: 'transparent', border: 'none', borderBottom: disputeFilter === tab.key ? '2px solid #dc2626' : '2px solid transparent', color: disputeFilter === tab.key ? 'white' : '#64748b', fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem', fontWeight: 700, padding: '0.625rem 1.25rem', cursor: 'pointer', marginBottom: '-1px', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  {tab.label}
                  {tab.count > 0 && <span style={{ background: '#dc2626', color: 'white', borderRadius: '99px', fontSize: '0.6rem', fontWeight: 800, padding: '1px 6px' }}>{tab.count}</span>}
                </button>
              ))}
            </div>

            {loadingDisputes ? (
              <div style={{ textAlign: 'center', padding: '4rem', color: '#64748b' }}>Loading disputes...</div>
            ) : filteredDisputes.length === 0 ? (
              <div style={{ ...s.card, textAlign: 'center', padding: '4rem' }}>
                <p style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</p>
                <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', fontWeight: 800, color: 'white', marginBottom: '0.5rem' }}>No {disputeFilter} disputes</p>
                <p style={{ color: '#64748b', fontSize: '0.875rem' }}>All clear — nothing to review</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {filteredDisputes.map((dispute) => {
                  const isOpen = expanded === dispute.bookingId;
                  const isResolvingThis = resolving[dispute.bookingId];
                  const statusColor = dispute.disputeStatus === 'open' ? '#f87171' : '#4ade80';
                  return (
                    <div key={dispute.bookingId} style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
                      <div onClick={() => setExpanded(isOpen ? null : dispute.bookingId)}
                        style={{ padding: '1.25rem 1.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', transition: 'background 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ width: 38, height: 38, borderRadius: '10px', background: dispute.disputeStatus === 'open' ? 'rgba(248,113,113,0.15)' : 'rgba(74,222,128,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0 }}>
                          {dispute.disputeStatus === 'open' ? '⚠️' : '✓'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.2rem', flexWrap: 'wrap' }}>
                            <p style={{ fontWeight: 700, color: 'white', fontSize: '0.9rem' }}>
                              {dispute.clientId?.slice(0, 8) || 'Client'} <span style={{ color: '#475569' }}>vs</span> {dispute.providerId?.slice(0, 8) || 'Provider'}
                            </p>
                            <span style={s.badge(statusColor)}>{dispute.disputeStatus}</span>
                          </div>
                          <p style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                            {dispute.service?.name || 'Service'} · {dispute.date} · <strong style={{ color: '#4ade80' }}>${dispute.service?.price || '—'}</strong>
                          </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                          <p style={{ fontSize: '0.72rem', color: '#475569' }}>
                            {dispute.disputeOpenedAt ? new Date(dispute.disputeOpenedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                          </p>
                          <span style={{ color: '#475569', fontSize: '0.875rem', transition: 'transform 0.2s', display: 'inline-block', transform: isOpen ? 'rotate(180deg)' : 'none' }}>▾</span>
                        </div>
                      </div>

                      {isOpen && (
                        <div style={{ borderTop: '1px solid #334155', padding: '1.5rem' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                            <div style={{ background: 'rgba(248,113,113,0.06)', border: '1px solid rgba(248,113,113,0.15)', borderRadius: '12px', padding: '1.25rem' }}>
                              <p style={{ ...s.label, color: '#f87171', marginBottom: '0.75rem' }}>👤 Client</p>
                              <p style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '0.625rem' }}>ID: {dispute.clientId}</p>
                              <div style={{ background: 'rgba(248,113,113,0.08)', borderRadius: '8px', padding: '0.875rem' }}>
                                <p style={{ fontSize: '0.7rem', color: '#f87171', fontWeight: 700, marginBottom: '0.3rem' }}>Dispute reason:</p>
                                <p style={{ fontSize: '0.875rem', color: 'white', lineHeight: 1.6 }}>"{dispute.disputeReason || 'No reason provided'}"</p>
                              </div>
                            </div>
                            <div style={{ background: 'rgba(74,222,128,0.04)', border: '1px solid rgba(74,222,128,0.12)', borderRadius: '12px', padding: '1.25rem' }}>
                              <p style={{ ...s.label, color: '#4ade80', marginBottom: '0.75rem' }}>🛠 Provider</p>
                              <p style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '0.625rem' }}>ID: {dispute.providerId}</p>
                              <div style={{ background: 'rgba(74,222,128,0.06)', borderRadius: '8px', padding: '0.875rem' }}>
                                <p style={{ fontSize: '0.7rem', color: '#4ade80', fontWeight: 700, marginBottom: '0.3rem' }}>Marked complete:</p>
                                <p style={{ fontSize: '0.875rem', color: 'white' }}>{dispute.completedAt ? new Date(dispute.completedAt).toLocaleString() : 'Unknown'}</p>
                              </div>
                            </div>
                          </div>

                          <div style={{ background: '#0f172a', borderRadius: '10px', padding: '1rem', marginBottom: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '0.75rem' }}>
                            {[{ l: 'Service', v: dispute.service?.name || '—' }, { l: 'Amount', v: `$${dispute.service?.price || '—'}` }, { l: 'Date', v: dispute.date || '—' }, { l: 'Payout', v: dispute.payoutStatus || '—' }, { l: 'Payment', v: dispute.paymentStatus || '—' }].map((row, i) => (
                              <div key={i}>
                                <p style={{ fontSize: '0.6rem', color: '#475569', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.2rem' }}>{row.l}</p>
                                <p style={{ fontSize: '0.8rem', color: '#cbd5e1', fontWeight: 600 }}>{row.v}</p>
                              </div>
                            ))}
                          </div>

                          {dispute.disputeStatus === 'open' ? (
                            <>
                              <div style={{ marginBottom: '1rem' }}>
                                <label style={s.label}>Admin Note (sent to both parties)</label>
                                <textarea value={adminNote[dispute.bookingId] || ''} onChange={e => setAdminNote(prev => ({ ...prev, [dispute.bookingId]: e.target.value }))}
                                  placeholder="Explain the decision to both parties..." rows={3}
                                  style={{ ...s.input, resize: 'vertical' }}
                                  onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#334155'}
                                />
                              </div>
                              <div style={{ display: 'flex', gap: '0.75rem' }}>
                                <button onClick={() => handleResolve(dispute, 'refund')} disabled={!!isResolvingThis}
                                  style={{ flex: 1, padding: '0.875rem', background: 'linear-gradient(135deg, #dc2626, #b91c1c)', border: 'none', borderRadius: '10px', color: 'white', fontWeight: 700, fontSize: '0.875rem', cursor: isResolvingThis ? 'not-allowed' : 'pointer', fontFamily: 'DM Sans, sans-serif', opacity: isResolvingThis ? 0.7 : 1, transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(220,38,38,0.3)' }}>
                                  {isResolvingThis === 'refund' ? 'Processing...' : '↩ Refund Client'}
                                </button>
                                <button onClick={() => handleResolve(dispute, 'release')} disabled={!!isResolvingThis}
                                  style={{ flex: 1, padding: '0.875rem', background: 'linear-gradient(135deg, #1a5c3a, #2d7a50)', border: 'none', borderRadius: '10px', color: 'white', fontWeight: 700, fontSize: '0.875rem', cursor: isResolvingThis ? 'not-allowed' : 'pointer', fontFamily: 'DM Sans, sans-serif', opacity: isResolvingThis ? 0.7 : 1, transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(26,92,58,0.3)' }}>
                                  {isResolvingThis === 'release' ? 'Processing...' : '✓ Release to Provider'}
                                </button>
                              </div>
                            </>
                          ) : (
                            <div style={{ background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.15)', borderRadius: '10px', padding: '1rem', display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                              <span style={{ fontSize: '1.25rem' }}>✓</span>
                              <div>
                                <p style={{ fontWeight: 700, color: '#4ade80', marginBottom: '0.15rem' }}>Resolved — {dispute.disputeOutcome === 'refund' ? 'Client refunded' : 'Provider paid'}</p>
                                <p style={{ fontSize: '0.78rem', color: '#64748b' }}>{dispute.disputeResolvedAt ? new Date(dispute.disputeResolvedAt).toLocaleString() : ''}{dispute.adminNote ? ` · "${dispute.adminNote}"` : ''}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── PROVIDERS TAB ── */}
        {activeTab === 'providers' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', fontWeight: 800, color: 'white', marginBottom: '0.25rem' }}>Providers</h1>
                <p style={{ color: '#64748b', fontSize: '0.875rem' }}>{providers.length} total providers on the platform</p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
              <StatCard label="Total" value={providers.length} color="#94a3b8" icon="🏪" />
              <StatCard label="Verified" value={verifiedProviders.length} color="#4ade80" icon="✓" />
              <StatCard label="Stripe Connected" value={connectedProviders.length} color="#a78bfa" icon="💳" />
              <StatCard label="Total Services" value={totalServices} color="#fbbf24" icon="✦" />
            </div>

            {/* Search + filter */}
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <input type="text" placeholder="Search providers..." value={provSearch} onChange={e => setProvSearch(e.target.value)}
                style={{ ...s.input, flex: 1, minWidth: '200px' }}
                onFocus={e => e.target.style.borderColor = '#60a5fa'} onBlur={e => e.target.style.borderColor = '#334155'}
              />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {[{ key: 'all', label: 'All' }, { key: 'verified', label: '✓ Verified' }, { key: 'connected', label: '💳 Stripe' }, { key: 'unverified', label: '○ Unverified' }].map(f => (
                  <button key={f.key} onClick={() => setProvFilter(f.key)}
                    style={{ background: provFilter === f.key ? '#60a5fa' : '#334155', border: 'none', color: provFilter === f.key ? 'white' : '#94a3b8', borderRadius: '8px', padding: '0.5rem 0.875rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s' }}
                  >{f.label}</button>
                ))}
              </div>
            </div>

            {loadingProviders ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>Loading providers...</div>
            ) : filteredProviders.length === 0 ? (
              <div style={{ ...s.card, textAlign: 'center', padding: '3rem' }}>
                <p style={{ color: '#64748b' }}>No providers found matching your filters</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                {filteredProviders.map((p, i) => (
                  <div key={p.providerId || i} style={{ ...s.card, padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', transition: 'all 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#475569'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#334155'}
                  >
                    <div style={{ width: 48, height: 48, borderRadius: '12px', overflow: 'hidden', background: '#1e3a5f', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', fontWeight: 800, color: '#60a5fa', border: '1px solid #334155' }}>
                      {p.profilePicUrl ? <img src={p.profilePicUrl} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (p.name?.[0]?.toUpperCase() || '?')}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '0.2rem', flexWrap: 'wrap' }}>
                        <p style={{ fontWeight: 700, color: 'white', fontSize: '0.9rem' }}>{p.name || 'Unnamed Provider'}</p>
                        {p.verified && <span style={s.badge('#4ade80')}>✓ Verified</span>}
                        {p.stripeOnboardingComplete && <span style={s.badge('#a78bfa')}>💳 Stripe</span>}
                      </div>
                      <p style={{ fontSize: '0.72rem', color: '#64748b' }}>
                        {p.serviceType || p.service_type || 'No category'} {p.location ? `· 📍 ${p.location}` : ''}
                        {p.overallRating ? ` · ★ ${p.overallRating}` : ''}
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', flexShrink: 0 }}>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: '0.72rem', color: '#64748b' }}>{p.services?.length || 0} services</p>
                        {p.totalRatingCount > 0 && <p style={{ fontSize: '0.68rem', color: '#64748b' }}>{p.totalRatingCount} reviews</p>}
                      </div>
                      <a href={`/provider/${p.providerId}`} target="_blank" rel="noreferrer"
                        style={{ background: '#334155', border: 'none', color: '#94a3b8', borderRadius: '7px', padding: '0.4rem 0.75rem', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', textDecoration: 'none', transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#475569'; e.currentTarget.style.color = 'white'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#334155'; e.currentTarget.style.color = '#94a3b8'; }}
                      >View ↗</a>
                      <button onClick={() => handleDeleteProvider(p.providerId, p.name)}
                        style={{ background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.2)', color: '#f87171', borderRadius: '7px', padding: '0.4rem 0.75rem', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = 'white'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.1)'; e.currentTarget.style.color = '#f87171'; }}
                      >Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPage;